import {
  Report_RoundingValue as RoundingValue,
  Report_ValueType as ValueType,
} from "@/services/graphql/reactQuery";

export const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  notation: "compact",
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

export const fullDollarFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export const currencyWithDecimalFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

export const numberFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 2,
});

// Handles rounding and precision for number value types.
// String and Date value types are preserved as they are passed in.
export const formatDisplayValue = (
  value: any,
  type: ValueType,
  precision?: number,
  rounding?: RoundingValue,
  region?: string,
  currencyRegion?: string
): string => {
  if (value == null) {
    return "-";
  }
  let displayValue = value.toString();

  if (type === ValueType.String || type === ValueType.Date) {
    return displayValue;
  }
  let finalValue = value;
  precision = precision != null ? precision : 0;
  const formatter = new Intl.NumberFormat(region != null ? region : "en-US", {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
    ...(type === ValueType.Dollar && {
      style: "currency",
      currency: currencyRegion != null ? currencyRegion : "USD",
    }),
    ...(type === ValueType.Ratio && { style: "percent" }),
  });
  finalValue = Number.parseFloat(finalValue);
  if (type === ValueType.Ratio) {
    displayValue = `${formatter.format(finalValue)}`;
  } else {
    rounding = rounding != null ? rounding : RoundingValue.None;
    switch (rounding) {
      case RoundingValue.Thousands:
        displayValue = `${formatter.format(finalValue / 1_000.0)}K`;
        break;
      case RoundingValue.Millions:
        displayValue = `${formatter.format(finalValue / 1_000_000.0)}M`;
        break;
      case RoundingValue.Billions:
        displayValue = `${formatter.format(finalValue / 1_000_000_000.0)}B`;
        break;
      default:
        displayValue = `${formatter.format(finalValue)}`;
    }
  }

  return displayValue;
};

export const currencyFormatterMillions = (value: number): string => {
  return `$${value.toFixed(1)}M`;
};

export const percentFormatter = (value: number): string => {
  return (value * 100).toFixed(2) + "%";
};

export function percentageFormatter(fractionalDigits?: number) {
  return new Intl.NumberFormat("en-US", {
    style: "percent",
    notation: "compact",
    minimumFractionDigits: fractionalDigits ?? 2,
    maximumFractionDigits: fractionalDigits ?? 2,
  });
}

export function dateFormatter(locale: string, useLongMonth?: boolean) {
  return new Intl.DateTimeFormat(locale, {
    month: useLongMonth ? "long" : "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

export function dateFormatterNumeric(dateString: string) {
  const date = new Date(dateString);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
}

export function largeNumberFormatter(num: number) {
  return num.toLocaleString("en-US");
}

export function formatCurrency(value: number, decimal: number = 1): string {
  if (value >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(decimal)}M`;
  } else if (value >= 1_000) {
    return `$${(value / 1_000).toFixed(decimal)}K`;
  } else {
    return `$${value.toFixed(2)}`;
  }
}
